import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import {
  CheckCircle, Plane, User, Mail, Phone, CreditCard,
  Download, Home, Loader2, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { flights as flightsApi } from '../lib/api';
import { useCurrency } from '../lib/currency-context';
import FlightCancellationPolicy from '../components/flights/FlightCancellationPolicy';

const FlightBookingSuccessPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bookingId = searchParams.get('id') || searchParams.get('order_id');
  const supplierPending = searchParams.get('supplier_pending') === '1';
  const supplierError = searchParams.get('error');

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };

  const cleanupSession = () => {
    sessionStorage.removeItem('selectedFlight');
    sessionStorage.removeItem('pendingFlightBookingId');
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    } else {
      setError('Rezervasyon bilgisi bulunamadı');
      setLoading(false);
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await flightsApi.getBooking(bookingId);
      setBooking(response.data);
      cleanupSession();
    } catch (err) {
      console.error('Error fetching flight booking:', err);
      setError('Rezervasyon detayları alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500 mb-4">{error || 'Rezervasyon bulunamadı'}</p>
            <Button onClick={() => navigate(`/${i18n.language}/flights`)}>
              <Home className="w-4 h-4 mr-2" />
              Uçuş Aramasına Dön
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = booking.flight_summary || {};
  const contact = booking.contact || {};
  const passengers = booking.passengers || [];
  const pnr = booking.pnr;
  const status = booking.status;
  const paymentStatus = booking.payment?.status;
  const isConfirmed = status === 'CONFIRMED' && pnr;
  const isSupplierFailed = status === 'supplier_failed' || supplierPending;
  const isPendingPayment = status === 'pending_payment' || paymentStatus === 'pending';
  const totalEur = Number(booking.total_with_fee || booking.base_total || 0);
  const handlingFee = Number(booking.handling_fee || 0);
  const emailSent = Boolean(booking.confirmation_email_sent);
  const reservationId = booking.id || bookingId;

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return format(new Date(iso), 'HH:mm');
    } catch {
      return iso;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return format(new Date(iso), 'd MMM yyyy', { locale: getDateLocale() });
    } catch {
      return iso;
    }
  };

  if (isPendingPayment && !isSupplierFailed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-amber-700 font-medium">Ödeme henüz onaylanmadı</p>
            <p className="text-sm text-gray-600">
              Rezervasyon kaydı oluştu ancak banka ödemesini onaylamadı. Lütfen tekrar deneyin.
            </p>
            <p className="text-xs text-gray-500">#{reservationId}</p>
            <Button onClick={() => navigate(`/${i18n.language}/flights/booking`)} className="w-full">
              Ödemeyi Tekrar Dene
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-12 print:py-4">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            isSupplierFailed ? 'bg-amber-100' : 'bg-green-100'
          }`}>
            {isSupplierFailed ? (
              <AlertTriangle className="w-12 h-12 text-amber-600" />
            ) : (
              <CheckCircle className="w-12 h-12 text-green-600" />
            )}
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${isSupplierFailed ? 'text-amber-800' : 'text-green-800'}`}>
            {isSupplierFailed
              ? 'Ödemeniz Alındı — Bilet İşleniyor'
              : 'Uçuş Biletiniz Onaylandı!'}
          </h1>
          <p className="text-gray-600">
            {isSupplierFailed
              ? 'Ödemeniz başarılı; bilet kesimi kısa süre içinde tamamlanacak. E-posta ile bilgilendirileceksiniz.'
              : emailSent
                ? 'Onay ve PNR bilgileriniz e-posta adresinize gönderildi.'
                : 'Onay e-postası kısa süre içinde gönderilecektir.'}
          </p>
          {isSupplierFailed && supplierError && (
            <p className="text-sm text-amber-700 mt-2">{decodeURIComponent(supplierError)}</p>
          )}
        </div>

        <Card className="mb-6 shadow-lg print:shadow-none">
          <CardHeader className="bg-gradient-to-r from-sky-500 to-cyan-600 text-white rounded-t-lg print:bg-sky-600">
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Plane className="w-5 h-5" />
                Uçuş Detayları
              </span>
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                #{reservationId}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {pnr && (
              <div className="text-center bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <p className="text-sm text-green-700 uppercase tracking-wider mb-1">PNR Kodu</p>
                <p className="text-4xl font-bold text-green-800 tracking-widest">{pnr}</p>
              </div>
            )}

            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="w-14 h-14 bg-sky-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-sky-700">
                  {summary.airline_code || 'XX'}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-2xl font-bold">{summary.origin || '—'}</p>
                    <p className="text-sm text-gray-500">{formatTime(summary.departure_time)}</p>
                    <p className="text-xs text-gray-400">{formatDate(summary.departure_time)}</p>
                  </div>
                  <Plane className="w-5 h-5 text-sky-500 shrink-0" />
                  <div className="text-right">
                    <p className="text-2xl font-bold">{summary.destination || '—'}</p>
                    <p className="text-sm text-gray-500">{formatTime(summary.arrival_time)}</p>
                    <p className="text-xs text-gray-400">{formatDate(summary.arrival_time)}</p>
                  </div>
                </div>
                {summary.brand_name && (
                  <p className="text-sm text-gray-500 mt-2">{summary.brand_name}</p>
                )}
                {summary.baggage_label && (
                  <p className="text-sm text-gray-500">Bagaj: {summary.baggage_label}</p>
                )}
              </div>
            </div>

            {passengers.length > 0 && (
              <div className="space-y-3 pb-4 border-b">
                <h4 className="font-medium text-gray-700">Yolcular</h4>
                {passengers.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{p.name} {p.surname}</span>
                    <span className="text-gray-400">
                      ({p.pax_type === 'ADULT' ? 'Yetişkin' : p.pax_type === 'CHILD' ? 'Çocuk' : 'Bebek'})
                    </span>
                  </div>
                ))}
              </div>
            )}

            {(contact.email || contact.name) && (
              <div className="space-y-2 pb-4 border-b text-sm">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{contact.name} {contact.surname}</span>
                </div>
                {contact.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{contact.email}</span>
                  </div>
                )}
                {contact.phone_number && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{contact.phone_country}{contact.phone_city}{contact.phone_number}</span>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  <span className="text-green-600 font-medium">Ödeme Tamamlandı</span>
                </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-sky-600">
                      {formatPrice(totalEur, 'EUR')}
                    </p>
                    {handlingFee > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Handling fee dahil</p>
                    )}
                  </div>
              </div>
            </div>

            <FlightCancellationPolicy
              airlineCode={summary.airline_code}
              className="print:break-inside-avoid"
            />
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
          <Button variant="outline" onClick={() => window.print()} className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Onayı Yazdır
          </Button>
          <Button
            onClick={() => navigate(`/${i18n.language}/flights`)}
            className="bg-sky-600 hover:bg-sky-700 flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Yeni Uçuş Ara
          </Button>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Sorularınız için: support@turkishemarket.com</p>
        </div>
      </div>
    </div>
  );
};

export default FlightBookingSuccessPage;
